// src/components/Broadcasts/Drawer.js - Version adaptée pour la duplication

import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Select } from 'antd';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const { OptGroup, Option } = Select;

function simulateWarmupDaysAndCurve(
  totalEmails,
  start,
  factor,
  max,
  limit = 60
) {
  let days = 0,
    current = start,
    assigned = 0;
  const curve = [];
  while (current < max && assigned < totalEmails && days < limit) {
    const emails = Math.min(Math.round(current), max, totalEmails - assigned);
    assigned += emails;
    curve.push(emails);
    current *= factor;
    days++;
  }
  while (assigned < totalEmails && days < limit) {
    const emails = Math.min(max, totalEmails - assigned);
    assigned += emails;
    curve.push(emails);
    days++;
  }
  return { days, curve };
}

function groupProvidersByServer(providers) {
  // Grouper les providers par serveur
  const grouped = providers.reduce((acc, provider) => {
    const serverKey = provider.server || 'Non assigné';
    if (!acc[serverKey]) {
      acc[serverKey] = [];
    }
    acc[serverKey].push(provider);
    return acc;
  }, {});

  // Trier chaque groupe par zone
  Object.keys(grouped).forEach(server => {
    grouped[server].sort((a, b) => (a.zone || '').localeCompare(b.zone || ''));
  });

  return grouped;
}

export default function BroadcastDrawer({
  playlistId,
  isOpen,
  onClose,
  broadcastToEdit,
  broadcastToDuplicate, // NOUVEAU : pour la duplication
  onSave,
}) {
  const drawerRef = useRef(null);

  const [playlistName, setPlaylistName] = useState('');
  const [userName, setUserName] = useState('');
  const [user, setUser] = useState('');
  const [broadcastName, setBroadcastName] = useState('');
  const [broadcastDescription, setBroadcastDescription] = useState('');
  const [smtpProviders, setSmtpProviders] = useState([]);
  const [smtpProviderId, setSmtpProviderId] = useState('');
  const [warmupParams, setWarmupParams] = useState({
    start: 100,
    factor: 1.1,
    max: 3960,
  });
  const [totalEmails, setTotalEmails] = useState(0);
  const [loading, setLoading] = useState(false);

  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const token = localStorage.getItem('prae.jwt');

  // Déterminer le mode : création, édition ou duplication
  const mode = broadcastToDuplicate
    ? 'duplicate'
    : broadcastToEdit
      ? 'edit'
      : 'create';

  useEffect(() => {
    if (!isOpen) return;
    const fetchData = async () => {
      try {
        const playlistRes = await fetch(
          `${process.env.REACT_APP_API_URL}/playlists/${playlistId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        const playlistData = await playlistRes.json();
        setPlaylistName(playlistData.name);

        const userRes = await fetch(
          `${process.env.REACT_APP_API_URL}/playlists/${playlistId}/user`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        const userData = await userRes.json();
        setUserName(`${userData.firstname} ${userData.lastname}`);
        setUser(userData);

        const smtpRes = await fetch(
          `${process.env.REACT_APP_API_URL}/smtp-providers`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        const providers = await smtpRes.json();
        // Filtrer les providers supprimés (sécurité côté frontend)
        setSmtpProviders(providers.filter(p => !p.deleted_at));

        const totalRes = await fetch(
          `${process.env.REACT_APP_API_URL}/playlists/${playlistId}/total-emails`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        const data = await totalRes.json();
        setTotalEmails(Number(data.total));

        const catRes = await fetch(
          `${process.env.REACT_APP_API_URL}/categories`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );
        setCategories(await catRes.json());
      } catch (e) {
        toast.error('Erreur lors du chargement des données');
      }
    };
    fetchData();
  }, [isOpen, playlistId]);

  useEffect(() => {
    if (isOpen) {
      if (broadcastToEdit) {
        // Mode édition
        setBroadcastName(broadcastToEdit.name || '');
        setBroadcastDescription(broadcastToEdit.description || '');
        setSmtpProviderId(broadcastToEdit.smtp_provider_id || '');
        setCategoryId(broadcastToEdit.category_id || '');
        setWarmupParams(
          broadcastToEdit.warmup_params || {
            start: 100,
            factor: 1.1,
            max: 3960,
          }
        );
      } else if (broadcastToDuplicate) {
        // Mode duplication - pré-remplir avec les données du broadcast original
        setBroadcastName(`${broadcastToDuplicate.name} (Copie)`);
        setBroadcastDescription(broadcastToDuplicate.description || '');
        setSmtpProviderId(broadcastToDuplicate.smtp_provider_id || '');
        setCategoryId(broadcastToDuplicate.category_id || '');

        // Pour la duplication, proposer de nouveaux paramètres de warmup par défaut
        // mais permettre de garder les anciens si souhaité
        setWarmupParams(
          broadcastToDuplicate.warmup_params || {
            start: 100,
            factor: 1.1,
            max: 3960,
          }
        );
      } else {
        // Mode création
        setBroadcastName('');
        setBroadcastDescription('');
        setSmtpProviderId('');
        setWarmupParams({ start: 100, factor: 1.1, max: 3960 });
        setCategoryId('');
      }
    }
  }, [isOpen, broadcastToEdit, broadcastToDuplicate]);

  const { days: nbJours, curve: warmupCurve } = useMemo(
    () =>
      simulateWarmupDaysAndCurve(
        totalEmails,
        warmupParams.start,
        warmupParams.factor,
        warmupParams.max,
        60
      ),
    [totalEmails, warmupParams.start, warmupParams.factor, warmupParams.max]
  );

  const groupedProviders = useMemo(() => {
    return groupProvidersByServer(smtpProviders);
  }, [smtpProviders]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'duplicate') {
        // Logique de duplication
        const duplicateData = {
          newBroadcastName: broadcastName,
          newBroadcastDescription: broadcastDescription,
          smtp_provider_id: smtpProviderId,
          category_id: categoryId,
          warmup_params: {
            initial_volume: warmupParams.start,
            max_volume: warmupParams.max,
            growth_rate: warmupParams.factor,
            plateau_days: 3, // Valeur par défaut, vous pouvez ajouter un champ pour cela
          },
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastToDuplicate.id}/duplicate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(duplicateData),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || 'Erreur lors de la duplication du broadcast'
          );
        }

        const result = await response.json();
        toast.success(
          `Broadcast dupliqué avec succès ! ${result.messagesCount} messages copiés.`
        );
      } else {
        // Logique création/édition existante
        const data = {
          broadcastName,
          broadcastDescription,
          playlistId,
          userId: user.id,
          smtp_provider_id: smtpProviderId,
          warmup_params: warmupParams,
          category_id: categoryId,
        };

        const url = broadcastToEdit
          ? `${process.env.REACT_APP_API_URL}/broadcasts/${broadcastToEdit.id}`
          : `${process.env.REACT_APP_API_URL}/broadcasts`;
        const method = broadcastToEdit ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(data),
        });

        if (!response.ok)
          throw new Error('Erreur lors de la sauvegarde du broadcast');

        toast.success(
          broadcastToEdit
            ? 'Broadcast modifié !'
            : 'Broadcast créé avec succès !'
        );
      }

      if (onSave) await onSave();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la sauvegarde du broadcast');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = event => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Déterminer le titre selon le mode
  const getTitle = () => {
    switch (mode) {
      case 'duplicate':
        return 'Dupliquer un Broadcast';
      case 'edit':
        return 'Modifier un Broadcast';
      default:
        return 'Créer un Nouveau Broadcast';
    }
  };

  // Déterminer le texte du bouton selon le mode
  const getButtonText = () => {
    if (loading) return 'Chargement…';
    switch (mode) {
      case 'duplicate':
        return 'Dupliquer le Broadcast';
      case 'edit':
        return 'Enregistrer les modifications';
      default:
        return 'Créer le Broadcast';
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 z-50 mt-16 transition-opacity ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'} flex justify-end`}
    >
      <ToastContainer position="top-center" autoClose={2000} hideProgressBar />
      <div
        ref={drawerRef}
        className={`bg-white pt-62 w-full max-w-2xl p-7 shadow-2xl transform transition-transform overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ maxHeight: '100vh' }}
      >
        <h2 className="text-2xl font-bold mb-6 text-gray-800">{getTitle()}</h2>

        {/* Message informatif pour la duplication */}
        {mode === 'duplicate' && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <div className="text-blue-600 mr-3 mt-0.5">ℹ️</div>
              <div>
                <h4 className="text-blue-800 font-semibold mb-1">
                  Duplication en cours
                </h4>
                <p className="text-blue-700 text-sm">
                  Les messages du broadcast original seront copiés. Un nouveau
                  plan de warmup sera généré avec vos paramètres.
                </p>
                {broadcastToDuplicate && (
                  <p className="text-blue-600 text-sm mt-1">
                    <strong>Source :</strong> {broadcastToDuplicate.name}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium text-gray-700">
                Playlist
              </label>
              <input
                type="text"
                value={playlistName}
                disabled
                className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-gray-700">
                Utilisateur
              </label>
              <input
                type="text"
                value={userName}
                disabled
                className="w-full p-2 border border-gray-300 rounded bg-gray-100 text-gray-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 font-medium text-gray-700">
                Nom du Broadcast
              </label>
              <input
                type="text"
                value={broadcastName}
                onChange={e => setBroadcastName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Nom du broadcast"
                required
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-gray-700">
                Description
              </label>
              <input
                type="text"
                value={broadcastDescription}
                onChange={e => setBroadcastDescription(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded"
                placeholder="Description"
              />
            </div>
          </div>

          <div>
            <label className="block mb-1 text-gray-700 font-semibold">
              Catégorie d'activité
            </label>
            <select
              value={categoryId}
              onChange={e => setCategoryId(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded"
              required
            >
              <option value="">Sélectionner une catégorie…</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 text-gray-700 font-semibold">
              Serveur SMTP
            </label>
            <Select
              showSearch
              allowClear
              placeholder="Sélectionner un serveur SMTP…"
              value={smtpProviderId || undefined}
              onChange={value => setSmtpProviderId(value ? Number(value) : '')}
              className="w-full"
              style={{ width: '100%' }}
              optionFilterProp="label"
              filterOption={(input, option) => {
                const label = option?.label || '';
                return label.toLowerCase().includes(input.toLowerCase());
              }}
            >
              {Object.entries(groupedProviders).map(([server, providers]) => (
                <OptGroup key={server} label={`Serveur: ${server}`}>
                  {providers.map(cfg => (
                    <Option
                      key={cfg.id}
                      value={cfg.id}
                      label={`${cfg.zone || ''} - ${cfg.name} (${cfg.host}:${cfg.port})`}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{cfg.name}</span>
                        <span className="text-xs text-gray-500">
                          Zone: {cfg.zone || 'N/A'} | {cfg.host}:{cfg.port}
                        </span>
                      </div>
                    </Option>
                  ))}
                </OptGroup>
              ))}
            </Select>
          </div>

          {/* Section warmup - affichée pour création et duplication */}
          {(mode === 'create' || mode === 'duplicate') && (
            <div className="bg-gray-50 rounded-xl p-4 border mt-2">
              <h3 className="font-semibold mb-2 text-gray-700 text-lg">
                {mode === 'duplicate'
                  ? 'Nouveaux Paramètres du Warmup'
                  : 'Paramètres du Warmup'}
              </h3>

              {mode === 'duplicate' && (
                <p className="text-sm text-gray-600 mb-4">
                  Vous pouvez ajuster les paramètres pour créer un plan de
                  warmup différent de l'original.
                </p>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 text-gray-600">
                    Nombre total d'emails
                  </label>
                  <input
                    type="number"
                    value={totalEmails}
                    className="w-full p-2 border border-gray-300 rounded bg-gray-100"
                    readOnly
                    disabled
                  />
                </div>
                <div>
                  <label className="block mb-1 text-gray-600">
                    Départ / jour (start)
                  </label>
                  <input
                    type="number"
                    value={warmupParams.start}
                    onChange={e =>
                      setWarmupParams({
                        ...warmupParams,
                        start: Number(e.target.value),
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded"
                    min={1}
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-gray-600">
                    Facteur exponentiel
                  </label>
                  <input
                    type="number"
                    value={warmupParams.factor}
                    step="0.01"
                    onChange={e =>
                      setWarmupParams({
                        ...warmupParams,
                        factor: Number(e.target.value),
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded"
                    min={1.01}
                    required
                  />
                </div>
                <div>
                  <label className="block mb-1 text-gray-600">
                    Cap max / jour
                  </label>
                  <input
                    type="number"
                    value={warmupParams.max}
                    onChange={e =>
                      setWarmupParams({
                        ...warmupParams,
                        max: Number(e.target.value),
                      })
                    }
                    className="w-full p-2 border border-gray-300 rounded"
                    min={1}
                    required
                  />
                </div>
              </div>

              <div className="mt-3 text-gray-800 font-medium">
                <span>Durée estimée du warmup : </span>
                <span className="text-purple-700 font-bold text-lg">
                  {nbJours} jours
                </span>
              </div>

              <div className="mt-2 text-gray-500 text-sm">
                <span className="font-semibold">
                  Emails/jour simulés (max 30 jours):{' '}
                </span>
                <span>
                  {warmupCurve.slice(0, 30).map((val, i) => (
                    <span key={i} className="inline-block px-1">
                      {val}
                    </span>
                  ))}
                  {warmupCurve.length > 30 && '…'}
                </span>
              </div>
            </div>
          )}

          <button
            type="submit"
            className={`w-full py-3 rounded-xl font-bold mt-4 transition ${
              mode === 'duplicate'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
            disabled={loading}
          >
            {getButtonText()}
          </button>
        </form>
      </div>
    </div>
  );
}
